import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Star, Clock, Phone, Car, Download } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Receipts are generated server-side; "download" opens the PDF URL in the browser.
// In the INSTALLED app the WebView only downloads via the system browser, which it
// only opens for a CROSS-ORIGIN URL - so on native we hit the backend directly
// (that's why the same-origin rewrite broke downloads). On the web we use the
// same-origin Vercel rewrite for a clean URL that hides the backend host.
function openReceipt(rideId: string) {
  if (!rideId) return;
  const url = Capacitor.isNativePlatform()
    ? `${API_BASE}/api/v1/public/receipt/${rideId}`
    : `${window.location.origin}/dl/receipt/${rideId}`;
  window.open(url, '_blank');
}

interface Props {
  activeRideId?: string;
  rideStatus?: string;
  assignedDriver?: any;
}

const statusLabel: Record<string, { text: string; color: string }> = {
  pending:     { text: 'Searching',  color: 'text-yellow-400 bg-yellow-400/10' },
  accepted:    { text: 'On the way', color: 'text-blue-400 bg-blue-400/10' },
  arrived:     { text: 'Driver here', color: 'text-purple-400 bg-purple-400/10' },
  in_progress: { text: 'On Trip',    color: 'text-green-400 bg-green-400/10' },
  completed:   { text: 'Completed',  color: 'text-white/40 bg-white/5' },
  cancelled:   { text: 'Cancelled',  color: 'text-red-400 bg-red-400/10' },
  rejected:    { text: 'Declined',   color: 'text-red-400 bg-red-400/10' },
};

const RideHistory: React.FC<Props> = ({ activeRideId, rideStatus, assignedDriver }) => {
  const [rides, setRides] = useState<any[]>([]);

  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('rideHistory') || '[]');
    setRides(history);
  }, [activeRideId, rideStatus]);

  const activeStatuses = ['pending', 'accepted', 'arrived', 'in_progress'];
  const isActive = activeRideId && rideStatus && activeStatuses.includes(rideStatus);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 pt-4 pb-24">
      <h2 className="text-white font-bold text-xl mb-4">Ride History</h2>
      {rides.length === 0 && !isActive && (
        <p className="text-white/50 text-sm text-center py-8">No rides yet. Book your first ride!</p>
      )}
      {rides.map((ride: any) => {
        const isCurrentRide = ride.id === activeRideId && isActive;
        const isCancelled = !isCurrentRide && ride.status === 'cancelled';
        const badge = isCurrentRide && rideStatus ? statusLabel[rideStatus] : statusLabel['completed'];
        const phone = isCurrentRide ? (assignedDriver?.phone || ride.driverPhone) : ride.driverPhone;
        const plate = isCurrentRide
          ? (assignedDriver?.driver_vehicles?.[0]?.plate_number || ride.driverPlate)
          : ride.driverPlate;
        const driverName = isCurrentRide ? (assignedDriver?.full_name || ride.driver) : ride.driver;

        return (
          <div key={ride.id} className={`rounded-xl p-4 mb-3 border ${isCurrentRide ? 'border-safaricab-gold/40 bg-safaricab-gold/5' : 'border-white/10 bg-safaricab-card'}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/50 text-xs">{ride.date}</span>
              <div className="flex items-center gap-2">
                {isCurrentRide && rideStatus && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.color}`}>
                    {badge.text}
                  </span>
                )}
                {/* Cancelled trips are never billed - show the badge, not a fare */}
                {isCancelled
                  ? <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusLabel.cancelled.color}`}>{statusLabel.cancelled.text}</span>
                  : <span className="text-safaricab-gold font-bold">KES {ride.price}</span>}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-white text-sm truncate">{ride.pickup}</span>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Navigation className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-white text-sm truncate">{ride.dropoff}</span>
            </div>

            {(driverName || phone || plate) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-white/60">
                {driverName && <span className="font-medium text-white/80">{driverName}</span>}
                {phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />{phone}
                  </span>
                )}
                {plate && (
                  <span className="flex items-center gap-1">
                    <Car className="w-3 h-3" />{plate}
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center justify-between text-xs text-white/50">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ride.duration} min</span>
                <span>{ride.distance} km</span>
                <span>{ride.rideType}</span>
              </div>
              {!isCurrentRide && !isCancelled && (
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-3 h-3 ${i < (ride.rating || 5) ? 'text-safaricab-gold fill-current' : 'text-white/20'}`} />
                  ))}
                </div>
              )}
            </div>

            {!isCurrentRide && !isCancelled && (
              <button
                onClick={() => openReceipt(ride.id)}
                className="mt-3 w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white/70 text-xs font-medium py-2 rounded-lg border border-white/10"
              >
                <Download className="w-3.5 h-3.5" /> Download Receipt
              </button>
            )}
          </div>
        );
      })}
    </motion.div>
  );
};

export default RideHistory;
